import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { isCampaignPastEndDate } from '../services/contentService';

const statusLabel = {
  active: { dot: 'bg-primary', label: 'Active' },
  funded: { dot: 'bg-primary', label: 'Funded' },
  completed: { dot: 'bg-muted-foreground', label: 'Done' },
  cancelled: { dot: 'bg-destructive', label: 'Cancelled' },
};

const CrowdfundCard = ({ project }) => {
  const percent = Math.min(project.progressPercentage ?? 0, 100);
  const ended = isCampaignPastEndDate(project.endDate);
  const daysLeft = project.endDate && !ended
    ? Math.max(
        0,
        Math.ceil(
          (new Date(project.endDate) - new Date()) / (1000 * 60 * 60 * 24)
        )
      )
    : null;

  const status = ended
    ? { dot: 'bg-muted-foreground', label: 'Ended' }
    : (statusLabel[project.status] ?? statusLabel.active);

  return (
    <Link
      to={`/crowdfund/${project.id}`}
      className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div
        className={cn(
          'relative aspect-[2/3] overflow-hidden rounded-lg bg-[hsl(var(--kinora-card-hi))]',
          'ring-1 ring-transparent transition-[box-shadow,ring-color] duration-300',
          'group-hover:ring-primary/25 group-hover:shadow-md'
        )}
      >
        {project.image ? (
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:group-hover:scale-100"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-card">
            <span className="text-3xl opacity-40">◐</span>
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

        <div className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/90 backdrop-blur-sm">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              status.dot,
              project.status === 'active' && !ended && 'animate-pulse'
            )}
          />
          {status.label}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white">
            {project.title}
          </h3>
          <div className="mt-2 h-[2px] w-full overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs tabular-nums text-white/70">
            {percent}% funded
            {daysLeft !== null && project.status === 'active' && !ended
              ? ` · ${daysLeft === 0 ? 'Ends today' : `${daysLeft}d left`}`
              : ''}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default CrowdfundCard;
